import { ChangeEvent, FC, useEffect, useState } from 'react';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { useMutation } from 'react-query';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { PhoneCallsApi } from '@frontend/api-phone-calls';
import { TagsUtils, TagsV2 } from '@frontend/api-tag';
import { generateTrackingId } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { TagSelectionComponents, TagSelectionHooks } from '@frontend/tag-selection';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Button, Chip, SpinningLoader, TextLink, useAlert, useTooltip } from '@frontend/design-system';
import { useActiveFilters } from '../hooks/use-active-filters';
import { useCallIntelDemoFlags } from '../hooks/use-call-intel-demo-flags';

interface TagsColumnProps {
  call: CallIntelTypes.CallRecord;
  onUpdateCallTags: (call: CallIntelTypes.CallRecord) => void;
  onOpenTagPanel?: () => void;
}
export const TagsColumn: FC<TagsColumnProps> = ({ call, onUpdateCallTags, onOpenTagPanel }) => {
  const { filters } = useActiveFilters();
  const { selectedOrgId } = useAppScopeStore();
  const { t } = useTranslation('analytics');
  const [isEditing, setIsEditing] = useState(false);
  const { Tooltip, tooltipProps, triggerProps } = useTooltip();
  const { isDemoAccount } = useCallIntelDemoFlags();
  const alert = useAlert();

  const { data: mappedTags = {}, isLoading: isTagDataLoading } = TagsV2.Queries.useListTagsQuery({
    request: { orgId: selectedOrgId, groupIds: filters.locations },
    options: {
      retry: false,
      select: (data) => {
        return data?.tags.reduce<Record<string, Tag>>((acc, tag) => {
          acc[tag.id] = tag;
          return acc;
        }, {});
      },
    },
  });

  const { mutate: applyTag, isLoading: isAddTagMutating } = useMutation({
    mutationFn: (tag: Tag) =>
      PhoneCallsApi.applyCallRecordTags({
        channelId: call.id ?? '',
        tagIds: [tag?.id],
        callRecordLocationId: call.locationId ?? '',
        startTime: call.startTime ?? '',
        isDemo: isDemoAccount,
      }),
    onSuccess: (_, addedTag) => {
      onUpdateCallTags({
        ...call,
        tagIds: [...(call.tagIds ?? []), addedTag.id],
      });
      alert.success(t('Tag added successfully.'));
    },
    onError: (error, addedTag) => {
      alert.error(t('Failed to add tag.'));
      sentry.warn({
        team: 'analytics',
        topic: 'call-intelligence',
        error: `Failed to add tag to call record ${error}`,
        addContext: {
          name: 'TagsData',
          context: {
            callId: call.id,
            tagId: addedTag.id,
            error,
          },
        },
      });
    },
  });

  const { mutate: removeTag, isLoading: isRemoveTagMutating } = useMutation({
    mutationFn: (tag: Tag) =>
      PhoneCallsApi.removeCallRecordTags({
        channelId: call.id ?? '',
        tagIds: [tag?.id],
        callRecordLocationId: call.locationId ?? '',
        startTime: call.startTime ?? '',
        isDemo: isDemoAccount,
      }),
    onSuccess: (_, removedTag) => {
      onUpdateCallTags({
        ...call,
        tagIds: (call.tagIds ?? []).filter((id) => id !== removedTag.id),
      });
      alert.success(t('Successfully removed tag.'));
    },
    onError: (error, removedTag) => {
      alert.error(t('Failed to remove tag.'));
      sentry.warn({
        team: 'analytics',
        topic: 'call-intelligence',
        error: `Failed to remove tag to call record ${error}`,
        addContext: {
          name: 'TagsData',
          context: {
            callId: call?.id,
            tagId: removedTag?.id,
            error,
          },
        },
      });
    },
  });

  const tagsList = call.tagIds?.map((id) => mappedTags[id]).filter(Boolean) ?? [];

  if (isTagDataLoading) {
    return null;
  }

  if (isRemoveTagMutating || isAddTagMutating) {
    return <SpinningLoader size='xs' />;
  }
  if (tagsList.length) {
    const firstTagData = tagsList[0];

    return (
      <div css={{ display: 'flex', gap: theme.spacing(1), alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip.Tag
          isRemovable
          onRemoveClick={() => {
            firstTagData && removeTag(firstTagData);
          }}
          trackingIds={{
            removeButton: generateTrackingId({
              isDemoAccount,
              component: 'call-records',
              context: 'remove-tag',
            }),
          }}
          css={{ width: 'auto' }}
          color={TagsUtils.convertStringToTagColor(firstTagData?.color ?? '')}
        >
          {firstTagData?.name ?? ''}
        </Chip.Tag>
        {tagsList.length > 1 && (
          <TextLink
            css={{
              ':hover': {
                textDecoration: 'none',
              },
            }}
            onClick={onOpenTagPanel}
            trackingId={generateTrackingId({
              isDemoAccount,
              component: 'call-records',
              context: 'tag-open-call-insight-action',
            })}
          >
            +{tagsList.length - 1}
          </TextLink>
        )}
      </div>
    );
  }

  return isEditing ? (
    <TagSelector
      existingTagIds={call.tagIds ?? []}
      onClose={() => {
        setIsEditing(false);
      }}
      onSelect={() => {
        setIsEditing(false);
      }}
      onApplyTag={applyTag}
    />
  ) : (
    <>
      <Button
        {...triggerProps}
        variant='secondary'
        css={{
          padding: theme.spacing(0.5),
          border: 'none',
        }}
        label={t('Tag Call')}
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
        trackingId={generateTrackingId({ isDemoAccount, component: 'call-records', context: 'tag-call-action' })}
      >
        <Icon name='label' color='light' />
      </Button>
      <Tooltip {...tooltipProps}>{t('Add tag')}</Tooltip>
    </>
  );
};

export const TagSelector = ({
  existingTagIds = [],
  onClose,
  onSelect,
  onApplyTag,
}: {
  existingTagIds?: string[];
  onClose?: () => void;
  onSelect?: (tag: Tag) => void;
  onApplyTag?: (tag: Tag) => void;
}) => {
  const { isDemoAccount } = useCallIntelDemoFlags();
  const [tagSearchValue, setTagSearchValue] = useState('');
  const { navigate } = useSettingsNavigate();

  const {
    popoverProps: tagPopoverProps,
    open: openTagSelection,
    isOpen: tagSelectionIsOpen,
    close: closeTagSelection,
    getTriggerProps: getTagSelectionTriggerProps,
  } = TagSelectionHooks.useTagSelectionPopover({
    onClose: () => {
      closeTagSelection();
      onClose?.();
    },
    onTagSelect: async (tag) => {
      closeTagSelection();
      onApplyTag?.(tag);
      onSelect?.(tag);
    },
    onTagCreate: (initName) => {
      navigate({
        to: '/tags/quick-create',
        context: {
          onCreate: (tag: Tag) => {
            onApplyTag?.(tag);
          },
          name: initName ? encodeURIComponent(initName) : '',
        },
      });
    },
    trackingIds: {
      selectTag: generateTrackingId({ isDemoAccount, component: 'call-records', context: 'select-tag' }),
      createTag: generateTrackingId({ isDemoAccount, component: 'call-records', context: 'create-tag' }),
      createNamedTag: generateTrackingId({ isDemoAccount, component: 'call-records', context: 'create-named-tag' }),
      openTagSettings: generateTrackingId({ isDemoAccount, component: 'call-records', context: 'open-tag-settings' }),
    },
    searchValue: tagSearchValue,
    tagIdsToExclude: [...existingTagIds, ''],
  });

  useEffect(() => {
    openTagSelection();
  }, [openTagSelection]);

  return (
    <span ref={getTagSelectionTriggerProps().ref}>
      <Chip.Tag
        editable
        color='gray'
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Escape') {
            closeTagSelection();
            onClose?.();
          }
          if (e.key === 'Enter' && tagSelectionIsOpen) {
            e.preventDefault();
            if (tagPopoverProps.tagsList?.[0]) {
              closeTagSelection();
              const tag = tagPopoverProps.tagsList[0];
              onApplyTag?.(tag);
              onSelect?.(tag);
            } else {
              navigate({
                to: '/tags/quick-create',
                context: { name: tagSearchValue ? encodeURIComponent(tagSearchValue) : '' },
              });
            }
          }
        }}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setTagSearchValue(e.currentTarget.value);
        }}
      />
      <TagSelectionComponents.TagSelectionPopover {...tagPopoverProps} returnFocus={false} initialFocus={-1} />
    </span>
  );
};
